import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import {
  ToastContext,
  type ToastContextValue,
  type ToastOptions,
  type ToastSeverity,
} from './toast-context';

/**
 * Long enough to read a sentence without hurrying, short enough that it is not
 * sitting over the page when the next thing is done. The pause on hover and on
 * focus is what makes a time limit acceptable under WCAG 2.2.1.
 */
const AUTO_HIDE_MS = 6000;

/**
 * The severity in words.
 *
 * Colour says which this is, and so does the Alert's icon. Colour on its own is
 * not enough under WCAG 1.4.1, and neither colour nor icon reaches somebody
 * listening to the page, so the word is there too.
 */
const SEVERITY_WORD: Record<ToastSeverity, string> = {
  success: 'Success',
  error: 'Error',
};

interface ActiveToast {
  /** Changes on every call, so a replacement restarts the timer. */
  id: number;
  message: string;
  severity: ToastSeverity;
}

/**
 * Shows short confirmations after an action, one at a time.
 *
 * Mounted once, around the whole app, because a message has to outlive the
 * screen that asked for it - a save that navigates away still needs to say it
 * worked.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ActiveToast | null>(null);

  const show = useCallback(({ message, severity = 'success' }: ToastOptions) => {
    // Numbering from the previous toast rather than a counter of its own means
    // a replacement is always a new key, so the six seconds start again instead
    // of the new message inheriting what was left of the old one's time.
    setToast((previous) => ({ id: (previous?.id ?? 0) + 1, message, severity }));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      <Snackbar
        // A new key remounts the snackbar, which both restarts the timer and
        // puts a fresh live region on the page - the same node with new text
        // inside it is not reliably announced when the text happens to repeat.
        key={toast?.id}
        open={Boolean(toast)}
        autoHideDuration={AUTO_HIDE_MS}
        // MUI pauses the timer while the pointer or the keyboard is on the
        // snackbar and resumes when it leaves, so this needs no help.
        onClose={(_event, reason) => {
          // A click anywhere else is not a dismissal. Somebody carrying on with
          // the page should not lose the message telling them what just
          // happened.
          if (reason === 'clickaway') return;
          setToast(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            // "status" rather than the Alert default of "alert": a confirmation
            // is announced when the reader reaches a natural break, and does
            // not interrupt what is being read.
            role="status"
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ width: '100%', alignItems: 'center' }}
          >
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
              {SEVERITY_WORD[toast.severity]}:
            </Box>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}
